// react
import { Suspense, useCallback, useContext, useEffect } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { Layout, Spin } from "antd";

import "./App.scss";
import { ROUTES } from "./constants/routes";
import { PATHS } from "./constants/paths";
import * as authActionTypes from "./constants/actionTypes/auth";
import { WEB_PORTALS } from "./constants/global";
import { AuthContext } from "./context/AuthContext";
import SideDrawerContextProvider from "./context/SideDrawerContext";
import { getAuthenticatedUser, getTokenValidStatus } from "./helpers/auth/auth";
import { getUserAccess } from "./services/user";
import Login from "./pages/Login/Login";
import Validate2fa from "./pages/validate2fa/Validate2fa";
import SideDrawer from "./components/SideDrawer/SideDrawer";
import PrivateRoutes from "./utiles/PrivateRoutes";
import NotFoundPath from "./utiles/NotFoundPath";
import AppLayout from "./components/AppLayout/AppLayout";

const App = () => {
  const {
    state: {
      user,
      isAuthenticated,
      loadingUserAccess,
      homePath,
      userAccess: { allowedPageList },
    },
    dispatch,
  } = useContext(AuthContext);

  const navigate = useNavigate();

  useEffect(() => {
    const authenticatedUser = getAuthenticatedUser();
    const isValidToken = getTokenValidStatus();

    if (authenticatedUser && isValidToken) {
      dispatch({
        type: authActionTypes.LOGIN,
        payload: JSON.parse(authenticatedUser),
      });
    } else if (authenticatedUser && !isValidToken) {
      dispatch({
        type: authActionTypes.LOGIN,
        payload: JSON.parse(authenticatedUser),
      });
      navigate(PATHS.validate2fa);
    } else {
      dispatch({ type: authActionTypes.LOGOUT });
      navigate(PATHS.login);
    }
  }, [dispatch, navigate]);

  const fetchUserAccess = useCallback(async () => {
    try {
      const {
        data: { portalList },
      } = await getUserAccess();

      const { portal, ...accessPayload } = portalList.find(
        ({ portal }) => portal === WEB_PORTALS.DASHBOARD
      );

      dispatch({
        type: authActionTypes.FETCH_USER_ACCESS_SUCCESS,
        payload: accessPayload,
      });
    } catch (err) {
      console.error(err);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && user.isValidToken) {
      fetchUserAccess();
    }
  }, [isAuthenticated, user.isValidToken, fetchUserAccess]);

  return (
    <SideDrawerContextProvider>
      <Layout>
        {isAuthenticated &&
          user.isValidToken &&
          !loadingUserAccess &&
          homePath && <SideDrawer />}

        <Suspense
          fallback={
            <Spin className="position-absolute top-50 start-50 translate-middle" />
          }
        >
          <Routes>
            {homePath && !loadingUserAccess && (
              <Route element={<PrivateRoutes />}>
                {ROUTES.filter((route) =>
                  allowedPageList?.includes(route.key)
                ).map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={<AppLayout WrappedComponent={route.page} />}
                  />
                ))}
              </Route>
            )}

            <Route path={PATHS.login} element={<Login />} />
            <Route path={PATHS.validate2fa} element={<Validate2fa />} />

            <Route path="*" element={<NotFoundPath />} />
          </Routes>
        </Suspense>
      </Layout>
    </SideDrawerContextProvider>
  );
};

export default App;
